import cloneDeep from 'lodash-es/cloneDeep';
import uniqBy from 'lodash-es/uniqBy';

import { CartItem, CartOrderDetails, CartStruct } from '@/models/cart';
import { Company } from '@/models/company';
import { currency } from '@/utils/i18n';

import { CartState } from './state';

export function organizedItems(_state: CartState, getters) {
  const organizedItems: object = {};

  getters.items.map((item: CartItem) => {
    const company = item.company;
    const seller = item.product.sellerId;

    organizedItems[company] = organizedItems[company] || {};
    organizedItems[company][seller] = organizedItems[company][seller] || [];
    organizedItems[company][seller].push(item);
  });

  return organizedItems;
}

/**
 * Returns an organized version of the cart with pre-calculated totals
 * @returns {CartStruct}
 */
export function organizedCart(state: CartState) {
  const subjects = ['total', 'shipping', 'subtotal', 'tax'];
  const defaultCurrency = currency.default;

  const organizedCart: CartStruct = {
    breakoutTaxes: [],
    buyerCarts: [],
    cartId: state.cartId,
    discounts: { amount: 0, currency: defaultCurrency },
    total: { amount: 0, currency: defaultCurrency },
    shipping: { amount: 0, currency: defaultCurrency },
    subtotal: { amount: 0, currency: defaultCurrency },
    tax: { amount: 0, currency: defaultCurrency },
  };

  state.orders.forEach((_order) => {
    const order = cloneDeep(_order);
    const buyer = order.buyer;

    // If this is the first order re-set currency
    if (organizedCart.buyerCarts.length === 0) {
      subjects.forEach((subject) => {
        organizedCart[subject].currency = order.total.currency;
      });

      organizedCart.discounts.currency = order.total.currency;
    }

    let buyerCart = organizedCart.buyerCarts.find((cart) => cart.buyer.id === buyer.id);

    if (!buyerCart) {
      buyerCart = {
        buyer,
        orders: [],
        itemCount: 0,
        total: { amount: 0, currency: order.total.currency },
      };

      organizedCart.buyerCarts.push(buyerCart);
    }

    /**
     * Group orders under the same seller, and sum-up their combined totals
     */
    const combinedOrders = buyerCart.orders.find((o) => o.seller.id === order.seller.id);

    if (combinedOrders) {
      combinedOrders.groupedOrders = combinedOrders.groupedOrders || [cloneDeep(combinedOrders)];

      subjects.forEach((subject) => {
        combinedOrders[subject].amount += order[subject].amount;
      });

      combinedOrders.discount.amount.amount += order.discount.amount.amount;
      combinedOrders.groupedOrders.push(order);
    } else {
      buyerCart.orders.push(order);
    }

    buyerCart.total.amount += order.total.amount;
    buyerCart.itemCount += order.items.length;

    subjects.forEach((subject) => {
      organizedCart[subject].amount += order[subject].amount;
    });

    organizedCart.discounts.amount += order.discount.amount.amount;

    order.breakoutTaxes.forEach((orderBreakoutTax) => {
      const breakoutTax = organizedCart.breakoutTaxes.find((bt) => bt.taxId === orderBreakoutTax.taxId);

      if (!breakoutTax) {
        organizedCart.breakoutTaxes.push(cloneDeep(orderBreakoutTax));
      } else {
        breakoutTax.amount.amount += orderBreakoutTax.amount.amount;
      }
    });
  });

  return organizedCart;
}

/**
 * Returns the total cart item count
 * @returns {number}
 */
export function itemCount(state: CartState) {
  return state.cartItemCount;
}

/**
 * Returns an array of buyer companies
 * @returns { CartBuyer[] }
 */
export function buyers(state: CartState) {
  return state.companies.filter((company) => company.buyer);
}

/**
 * @param {number} companyId id of company
 */
export function companyById(state: CartState) {
  return (companyId: number) => {
    return state.companies.find((company) => company.id === companyId);
  };
}

/**
 * @param {number} cartItemId id of cart item
 */
export function cartItemById(_state: CartState, getters) {
  return (cartItemId: number) => {
    return getters.items.find((item: CartItem) => item.id === cartItemId);
  };
}

/**
 * @param {number} cartItemId id of cart item
 * @returns {String} Index of cart item in array
 */
export function itemIndexByCartId(_state: CartState, getters) {
  return (cartItemId: number) => {
    return getters.items.map((item: CartItem) => item.id).indexOf(cartItemId);
  };
}

export function uniqueProducts(_state: CartState, getters) {
  return uniqBy(getters.items, (item: CartItem) => item.product.id).map((item) => item.product);
}

export function subtotal(_state: CartState, getters) {
  return getters.items.reduce((acc, item: CartItem) => {
    return acc + item.subtotal.amount;
  }, 0);
}

export function companiesInCart(_state: CartState, getters) {
  return uniqBy(getters.items, (item: CartItem) => item.company).map((item) => item.company);
}

export const getPlacedOrdersByBuyer = (state: CartState) => {
  const placedOrdersByBuyer = {};

  state.placedOrders.forEach((order) => {
    const buyerExists = placedOrdersByBuyer[order.buyer];
    const buyer = state.companies.find((c: Company) => c.id === order.buyer);
    const orderWithSlug = { ...order, buyerSlug: (buyer || {}).slug };

    if (buyerExists) {
      placedOrdersByBuyer[order.buyer].orders.push(orderWithSlug);
    } else {
      placedOrdersByBuyer[order.buyer] = {
        buyer,
        cartId: state.cartId,
        orders: [orderWithSlug],
      };
    }
  });

  return placedOrdersByBuyer;
};

export function ordersSubtotal(state: CartState) {
  return state.orders.reduce((acc, order) => {
    return acc + order.subtotal.amount;
  }, 0);
}

/**
 * Build the promo codes array from the state.
 * @returns {array} Promo codes array.
 */
export const promoCodes = (state: CartState) => {
  const promoCodes = Object.keys(state.orderDetails).reduce(
    (promoCodes: { orderId: string; promoCodeId: number }[], orderId: string) => {
      const details: CartOrderDetails = state.orderDetails[orderId];

      if (details?.promoCodeId) {
        promoCodes.push({
          orderId,
          promoCodeId: details.promoCodeId,
        });
      }

      return promoCodes;
    },
    [],
  );

  return promoCodes;
};

/**
 * Build the orders items object.
 * @returns {array} Order items array.
 */
export const items = (state: CartState) => {
  return state.orders.reduce((acc, order) => acc.concat(order.items), [] as CartItem[]);
};

/**
 * Get the cart id from the state.
 * @param state
 * @returns {string} Cart id.
 */
export const cartId = (state: CartState) => {
  return state.cartId;
};
